export type RegistrationSourceContext = {
  schema_version: 1
  captured_at: string
  source_type: 'paid' | 'organic' | 'referral' | 'direct'
  landing_id: string
  landing_title: string | null
  landing_path: string | null
  landing_version: string
  landing_kind: string
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  has_yclid: boolean
}

const storageKey = 'cardreplay.registration-source.v1'
const paidMediums = new Set([
  'cpc',
  'ppc',
  'paid',
  'paidsearch',
  'display',
  'retargeting',
])
const searchHosts = ['yandex.', 'google.', 'bing.com', 'mail.ru', 'rambler.ru']

const clean = (value: string | null, limit: number) => {
  const normalized = value?.trim().slice(0, limit)
  return normalized && /^[A-Za-z0-9._-]+$/.test(normalized) ? normalized : null
}

function sourceType(url: URL, referrer: string) {
  const medium = url.searchParams.get('utm_medium')?.toLowerCase() ?? ''
  if (url.searchParams.has('yclid') || paidMediums.has(medium))
    return 'paid' as const
  if (medium === 'organic') return 'organic' as const
  if (!referrer) return 'direct' as const
  try {
    const host = new URL(referrer).hostname.toLowerCase()
    if (host === url.hostname.toLowerCase()) return 'direct' as const
    if (searchHosts.some((candidate) => host.includes(candidate)))
      return 'organic' as const
    return 'referral' as const
  } catch {
    return 'direct' as const
  }
}

function readStored(): RegistrationSourceContext | null {
  try {
    const parsed = JSON.parse(
      sessionStorage.getItem(storageKey) ?? 'null',
    ) as Partial<RegistrationSourceContext> | null
    if (parsed?.schema_version !== 1 || typeof parsed.captured_at !== 'string')
      return null
    if (
      !['paid', 'organic', 'referral', 'direct'].includes(
        parsed.source_type ?? '',
      )
    )
      return null
    if (
      typeof parsed.landing_id !== 'string' ||
      typeof parsed.landing_version !== 'string'
    )
      return null
    return {
      ...(parsed as RegistrationSourceContext),
      landing_title:
        typeof parsed.landing_title === 'string' ? parsed.landing_title : null,
      landing_path:
        typeof parsed.landing_path === 'string' ? parsed.landing_path : null,
    }
  } catch {
    return null
  }
}

export function captureRegistrationSource(
  landingId = 'cardreplay_app',
  landingVersion = 'v2',
  landingKind = 'app',
  landingTitle = 'CardReplay',
  landingPath: string | null = null,
) {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  const existing = readStored()
  if (existing) return existing

  const context: RegistrationSourceContext = {
    schema_version: 1,
    captured_at: new Date().toISOString(),
    source_type: sourceType(url, document.referrer),
    landing_id: landingId.slice(0, 100),
    landing_title:
      landingTitle.trim().replace(/\s+/g, ' ').slice(0, 200) || null,
    landing_path: landingPath?.slice(0, 200) ?? null,
    landing_version: landingVersion.slice(0, 100),
    landing_kind: landingKind.slice(0, 30),
    utm_source: clean(url.searchParams.get('utm_source'), 100),
    utm_medium: clean(url.searchParams.get('utm_medium'), 100),
    utm_campaign: clean(url.searchParams.get('utm_campaign'), 150),
    utm_content: clean(url.searchParams.get('utm_content'), 150),
    has_yclid: url.searchParams.has('yclid'),
  }
  sessionStorage.setItem(storageKey, JSON.stringify(context))
  return context
}

export function getRegistrationSource() {
  if (typeof window === 'undefined') return null
  return readStored() ?? captureRegistrationSource()
}
